import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DescriptionIcon from "@mui/icons-material/Description";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import PersonIcon from "@mui/icons-material/Person";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SecurityIcon from "@mui/icons-material/Security";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PropTypes from "prop-types";

import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { addEmployeeQuickLinksFromFAC, findFeatureByKey } from "../../util/featureAccess/featureAccessUtils";

const iconMap = {
  Work: WorkOutlineOutlinedIcon,
  PlaylistAddCheck: PlaylistAddCheckIcon,
  FormatListNumbered: FormatListNumberedIcon,
  CalendarToday: CalendarTodayIcon,
  ClearAll: ClearAllIcon,
  AccessTime: AccessTimeIcon,
  SwapHoriz: SwapHorizIcon,
  Security: SecurityIcon,
  Person: PersonIcon,
  Description: DescriptionIcon,
  ExitToApp: ExitToAppIcon,
  SummarizeOutlined: SummarizeOutlinedIcon,
};

const LinkIcon = ({ iconName, sx }) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent sx={sx} /> : null;
};

const DashboardEmployeeQuickLinksCard = (props) => {
  const buildQuickLinks = () => {
    // Client metadata for quick links to control sort order and add an icon
    let quickLinks = [
      { key: "myBids", icon: "Work", iconStyle: { color: "secondary.swaWhite" } },
      { key: "myDeclarations", icon: "SummarizeOutlined", iconStyle: { color: "secondary.swaWhite" } },
      { key: "lwopTowopEmployee", icon: "ExitToApp", iconStyle: { color: "secondary.swaWhite" } },
      { key: "myPermanentBid", icon: "PlaylistAddCheck", iconStyle: { color: "secondary.swaWhite" } },
      { key: "mySeniorityList", icon: "FormatListNumbered", iconStyle: { color: "secondary.swaWhite" } },
      { key: "employeeTimeOff", icon: "AccessTime", iconStyle: { color: "secondary.swaWhite" } },
      { key: "employeeTrades", icon: "SwapHoriz", iconStyle: { color: "secondary.swaWhite" } },
      { key: "votSignUpList", icon: "SummarizeOutlined", iconStyle: { color: "secondary.swaWhite" } },
    ].filter(Boolean);

    for (const quickLink of quickLinks) {
      const feature = findFeatureByKey(props.userFeatures, quickLink.key);
      quickLink.feature = feature;
    }

    // Add quick links sourced via FAC response, if a feature is not listed above then it will not have an icon or controlled sorting
    const finalQuickLinks = addEmployeeQuickLinksFromFAC(props.userFeatures, quickLinks, props.isDesktopView);

    return finalQuickLinks;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: "secondary.swaGray1",
        borderRadius: "5px",
        justifySelf: "end",
        width: "100%",
        ...(props.isDesktopView ? { maxWidth: "450px" } : {}),
      }}
    >
      <Box
        sx={{
          textAlign: "left",
          marginLeft: "1.5rem",
          marginTop: "1rem",
          marginRight: "1.5rem",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Quick Links
        </Typography>
        <Divider />
      </Box>
      <List sx={{ paddingTop: 0 }}>
        {buildQuickLinks().map((link, index) =>
          link.divider ? (
            <Divider key={index} variant="middle" />
          ) : (
            link?.feature?.path &&
            link?.feature?.isHomeButton && (
              <Tooltip key={link.feature.key} title={link.feature.description} placement="right">
                <ListItem>
                  <ListItemButton
                    href={`/#${link.feature.path}`}
                    sx={{
                      backgroundColor: "primary.swaBlue",
                      borderRadius: "5px",
                      ":hover": { backgroundColor: "secondary.swaDarkBlue" },
                    }}
                  >
                    <ListItemIcon>
                      <LinkIcon iconName={link.icon} sx={link.iconStyle} />
                    </ListItemIcon>
                    <ListItemText
                      primary={link.feature.label}
                      primaryTypographyProps={{ fontWeight: "bold", fontSize: "14px", color: "secondary.swaWhite" }}
                      sx={{ maxWidth: "140px" }}
                    />
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            )
          )
        )}
      </List>
    </Paper>
  );
};

DashboardEmployeeQuickLinksCard.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  userFeatures: PropTypes.array.isRequired,
};

export default DashboardEmployeeQuickLinksCard;
